<script setup>
import { ref, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { DxList } from 'devextreme-vue/list'

import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { chatActionServiceEnum } from '@/stores/chat.store'

import { useConfirmedChatActions } from '@/composables/chat-actions'
import { useODataSource } from '@/composables/data-grid'
import { useExpenseClaimActions } from '@/composables/expense-claim'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import ExpenseClaimDialog from '@/components/accounting/ExpenseClaimDialog.vue'
import ExpenseClaimListItem from '@/components/accounting/ExpenseClaimListItem.vue'
import MyExpensesPanel from '@/components/accounting/MyExpensesPanel.vue'

const props = defineProps({
  scope: {
    type: Number,
    required: true,
  },
})

const i18n = getI18n()
const route = useRoute()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const { canCreate } = useExpenseClaimActions()
const { isMobile } = useDetectDevice()
const { expenseClaimScopeEnum } = accountingStore

const isStaffScope = props.scope === expenseClaimScopeEnum.STAFF
const listContainer = ref(null)
let listInstance = null
const openedClaim = ref(null)

const showUnclaimed = ref(false)
const unclaimedStaff = ref(null)
const expensesPanel = ref(null)

const dataSource = useODataSource(accountingStore.api.getAbsoluteUrl() + accountingStore.api.expenseClaimsUrl, {
  searchExpr: 'search_field',
  customParamsFn: () => ({ scope: props.scope, with_unclaimed: true, unclaimed_by_staff: webSiteStore.expenseClaimsUnclaimedByStaff }),
})

const reloadAsync = () => dataSource.reload()

const onUnclaimedByStaffChange = (value) => {
  webSiteStore.expenseClaimsUnclaimedByStaff = value
  reloadAsync()
}

useConfirmedChatActions(chatActionServiceEnum.EXPENSES, reloadAsync)

const showCreatedAsync = async(claim) => {
  webSiteStore.activateUpdatedDateSort(route.name)
  dataSource.sort(webSiteStore.buildSortOrderBy(route.name))
  dataSource.pageIndex(0)
  await reloadAsync()
  await highlightRowAsync(claim.id, listContainer)
  listInstance?.repaint()
}

const openNew = () => {
  if (canCreate()) openedClaim.value = {}
}

const openUnclaimedAsync = async() => {
  unclaimedStaff.value = webSiteStore.staffId
  showUnclaimed.value = true
  await nextTick()
  return expensesPanel.value
}

const openCamera = async() => (await openUnclaimedAsync())?.openCamera()
const openNewExpense = async() => (await openUnclaimedAsync())?.openNewExpense()

const openClaimAsync = async(claimId) => {
  openedClaim.value = await accountingStore.fetchExpenseClaimByIdAsync(claimId)
}

const onItemClick = (item) => {
  if (item.is_unclaimed) {
    unclaimedStaff.value = item.staff
    showUnclaimed.value = true
  }
  else openedClaim.value = item
}

const onUnclaimedHide = () => {
  showUnclaimed.value = false
  reloadAsync()
}

const onClaimSaved = async(claim) => {
  openedClaim.value = null
  showUnclaimed.value = false
  if (claim) await showCreatedAsync(claim)
  else await reloadAsync()
}

const onClaimChanged = async() => {
  openedClaim.value = null
  await reloadAsync()
}

webSiteStore.fabActions = [
  { id: 'add', icon: 'mdi-plus', onClick: openNew },
  { id: 'add-expense', icon: 'mdi-cash-plus', onClick: openNewExpense },
  { id: 'quick-expense', icon: 'mdi-camera', onClick: openCamera },
]

defineExpose({ openClaimAsync, reloadAsync })
</script>

<template>
  <div class="column no-wrap">
    <expense-claim-dialog
      :claim="openedClaim"
      @saved="onClaimSaved"
      @changed="onClaimChanged"
      @cancel="openedClaim = null"
    />
    <div class="row items-center no-wrap">
      <filter-sort-selector class="col" :data-source="dataSource" hide-archived>
        <template v-if="isStaffScope && webSiteStore.canApproveExpenseClaims" #filterSecondRow>
          <q-toggle
            :model-value="webSiteStore.expenseClaimsUnclaimedByStaff"
            :label="i18n.t('common.expensesWithoutClaimByStaff')"
            color="primary"
            @update:model-value="onUnclaimedByStaffChange"
          />
        </template>
      </filter-sort-selector>
    </div>
    <q-separator />
    <q-dialog
      :model-value="showUnclaimed"
      class="dialog-md"
      :maximized="isMobile"
      @hide="onUnclaimedHide"
    >
      <q-card class="dialog-card-flex">
        <navigate-back :text="i18n.t('common.expensesWithoutClaim')" @cancel="showUnclaimed = false" />
        <my-expenses-panel
          ref="expensesPanel"
          class="col"
          :staff="unclaimedStaff"
          @claim-saved="onClaimSaved"
        />
      </q-card>
    </q-dialog>
    <q-card flat class="col q-pa-none" style="min-height: 0;">
      <q-card-section ref="listContainer" class="full-height column no-wrap q-pa-none">
        <dx-list
          class="col events-list"
          style="min-height: 0;"
          :data-source="dataSource"
          page-load-mode="scrollBottom"
          item-template="item"
          selection-mode="none"
          :active-state-enabled="false"
          @initialized="e => listInstance = e.component"
        >
          <template #item="{ data: claim }">
            <expense-claim-list-item
              :item="claim"
              :show-staff="isStaffScope"
              show-status
              :class="{ 'row-highlight': claim.id === highlightedRowId }"
              @click="onItemClick(claim)"
            />
          </template>
        </dx-list>
      </q-card-section>
    </q-card>
  </div>
</template>
